import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';

import { AuthService } from '../auth/auth.service';
import { EntityService } from '../entity/entity.service';

/**
 * Allow only platform administrators — users for whom the backend surfaces a
 * `system` context with NO ops_tier (the `capx-admin` role). Internal Ops staff
 * also carry a `system` context, but with an `ops_tier` set; they belong on the
 * Ops surface, so they are redirected to `/ops/dashboard` rather than admitted.
 * Everyone else goes to `/dashboard`.
 *
 * This is the client-side companion to the backend `IsSystemAdmin` permission
 * (which is the real gate — it now requires the platform-admin role); it keeps
 * admin-only pages off non-admins' radar and avoids rendering a page that would
 * only ever 403.
 */
export const adminGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const entityService = inject(EntityService);
  const router = inject(Router);

  if (!authService.isAuthenticated()) {
    return router.createUrlTree(['/auth/login']);
  }
  if (!authService.profileCompleted()) {
    return router.createUrlTree(['/auth/complete-profile']);
  }

  return entityService.getContexts().pipe(
    map((res) => {
      // Mirror the backend `is_platform_admin`: the `capx-admin` system role is the
      // admin signal, NOT `ops_tier == null`. `ops_tier` is a user-level field the
      // backend stamps on every system context, so a dual-hat admin (also on Ops)
      // carries it on their `capx-admin` context too — keying on it would lock them
      // out. See entity.service.ts `isAdmin` for the full note.
      const isPlatformAdmin = res.contexts.some(
        (c) => c.type === 'system' && c.role === 'capx-admin',
      );
      if (isPlatformAdmin) {
        return true;
      }
      // Ops staff (system context WITH an ops_tier) belong on the Ops surface.
      const isOps = res.contexts.some((c) => c.type === 'system' && c.ops_tier != null);
      return router.createUrlTree([isOps ? '/ops/dashboard' : '/dashboard']);
    }),
    catchError(() => of(router.createUrlTree(['/dashboard']))),
  );
};
